import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Select, Tooltip, Divider, InputNumber, Segmented, Input } from "antd";
import { ThunderboltOutlined, DownloadOutlined, CloseOutlined, LockOutlined, UnlockOutlined, CheckCircleFilled, CloudUploadOutlined, FullscreenOutlined, FullscreenExitOutlined, SettingOutlined, CheckOutlined } from "@ant-design/icons";
import { IonIcon } from "@ionic/react";
import { diamondOutline, documentOutline, imageOutline } from "ionicons/icons";
import StitchPreviewCanvas from "../SvgEditor/components/StitchPreviewCanvas";
import { parseEmbroideryFile } from "../SvgEditor/components/stitchParser";
import { describeFabricCompensation } from "./fabricCompensation";
import StitchArtworkExport from "./StitchArtworkExport";
import { useAuth } from "../../contexts/AuthContext";
import { isNativeiOS } from "../../lib/utils/platform";
import { isEmbroideryTrace } from "../../config/appVariant";
import IOSPaywall from "../PaywallModal/IOSPaywall";
import PaywallModal from "../PaywallModal/PaywallModal";

const MACHINE_FORMATS = [
  { value: "dst", label: "DST — Tajima (most universal)", popular: true },
  { value: "pes", label: "PES — Brother", popular: true },
  { value: "jef", label: "JEF — Janome", popular: true },
  { value: "vp3", label: "VP3 — Pfaff" },
  { value: "exp", label: "EXP — Melco" },
];

// Mirrors StitchSidebar's HOOP_SIZES so changes here stay in sync with the
// editor's right-rail hoop selector (and importantly its index ordering, which
// is what gets persisted to Firestore).
const HOOP_SIZES = [
  { label: "4×4 in (100×100mm)", width: 100, height: 100 },
  { label: "5×7 in (130×180mm)", width: 130, height: 180 },
  { label: "6×10 in (160×260mm)", width: 160, height: 260 },
  { label: "8×8 in (200×200mm)", width: 200, height: 200 },
  { label: "Custom", width: null, height: null },
];

// Keep in sync with StitchEditor/index.jsx. Machines refuse to display a design
// that reaches the hoop's nominal edge (usable field is a few mm smaller), and
// pull-comp/expand/satin push stitches slightly past the SVG bounds, so we fit
// designs into the hoop minus this margin (per side) to stay visible.
const HOOP_SAFETY_MARGIN_MM = 2;
const safeHoopDim = (mm) => Math.max(10, mm - HOOP_SAFETY_MARGIN_MM * 2);

// Fabric presets — enriched with the friendly metadata used by the wizard
// fabric cards (emoji, plain-language description, recommended needle).
// Fabric only drives machine-setup guidance + a relative compensation pass
// (see fabricCompensation.js). It does not store absolute stitch settings.
const FABRIC_PRESETS = [
  { value: "cotton",     label: "🧵 Cotton / Light",  description: "T-shirts, light linens",     needle: "75/11" },
  { value: "poly_blend", label: "👕 Poly Blend",      description: "Polos, athletic wear",       needle: "75/11" },
  { value: "fleece",     label: "🧥 Fleece / Hoodie", description: "Sweatshirts, heavy knits",   needle: "80/12" },
  { value: "denim",      label: "👖 Denim / Canvas",  description: "Jeans, bags, heavy fabrics", needle: "90/14" },
  { value: "hat",        label: "🧢 Hat / Cap",       description: "Structured caps, visors",    needle: "90/14" },
  { value: "custom",     label: "⚙️ Custom",          description: "Manual control",             needle: null },
];

// Wizard steps
const STEPS = [
  { num: 1, label: "Format & Size" },
  { num: 2, label: "Preview & Export" },
];

// Top-level tabs — mirrors the SVG editor's export modal. "stitch" is the
// machine-file wizard; the other two export the design artwork as-is.
const FORMAT_TABS = [
  { key: "stitch", label: "Stitch File", icon: diamondOutline },
  { key: "svg", label: "SVG", icon: documentOutline },
  { key: "png", label: "PNG", icon: imageOutline },
];

/**
 * Derive machine setup recommendations from all available parameters.
 * When stitchData is present (post-generation) it uses actual stitch count,
 * color changes, density, and real dimensions for much richer advice.
 */
function computeMachineRec({ fabric, stitchType, targetW, targetH, stitchData }) {
  const isSatin  = stitchType === "satin";
  const isRunning = stitchType === "running";

  // Actual values — prefer stitch-file measurements when available
  let stitchCount = 0;
  let colorChanges = 0;
  let designW = targetW || 0;
  let designH = targetH || 0;
  let jumpRatio = 0;

  if (stitchData?.stitches?.length > 0) {
    const all     = stitchData.stitches;
    const needles = all.filter((s) => !s.jump);
    const jumps   = all.filter((s) => s.jump);
    stitchCount   = needles.length;
    jumpRatio     = all.length > 0 ? jumps.length / all.length : 0;
    // Count unique color index values (each change = operator must re-thread)
    const colorSet = new Set(all.map((s) => s.colorIndex));
    colorChanges   = colorSet.size;
    // DST/PES native units are 0.1 mm
    designW = Math.round((stitchData.maxX - stitchData.minX) * 0.1);
    designH = Math.round((stitchData.maxY - stitchData.minY) * 0.1);
  }

  const isLarge    = Math.max(designW, designH) > 120;
  const isSmall    = Math.max(designW, designH) < 40;
  const isDense    = stitchCount > 30000;
  const isVeryDense = stitchCount > 60000;
  const manyColors = colorChanges > 5;

  // ── Stabilizer ──────────────────────────────────────────────────────────
  const stabMap = {
    cotton:     { weight: isVeryDense ? "Heavy" : isDense ? "Medium-Heavy" : "Medium", type: "Tearaway" },
    poly_blend: { weight: isDense ? "Medium" : "Light", type: "Cutaway" },
    fleece:     { weight: "Medium", type: "Adhesive Tearaway" },
    denim:      { weight: "Heavy", type: "Cutaway" },
    hat:        { weight: "Medium", type: "Self-Adhesive Tearaway" },
    custom:     { weight: isDense ? "Heavy" : "Medium", type: "Cutaway" },
  };
  const stabilizer = stabMap[fabric] || stabMap.cotton;

  // ── Needle ───────────────────────────────────────────────────────────────
  let needle;
  if (fabric === "denim")                             needle = { type: "Sharp",      size: "90/14" };
  else if (fabric === "hat")                          needle = { type: "Embroidery", size: "75/11" };
  else if (isSatin || isRunning || isSmall)           needle = { type: "Embroidery", size: "75/11" };
  else                                                needle = { type: "Embroidery", size: "80/12" };

  // ── Tension ──────────────────────────────────────────────────────────────
  let tension;
  if (fabric === "denim")      tension = { top: "4–5",     bobbin: "Normal (3–4)"   };
  else if (isRunning)          tension = { top: "3–4",     bobbin: "Normal (3–3.5)" };
  else if (isSatin)            tension = { top: "3–4",     bobbin: "Loose (2.5–3)"  };
  else                         tension = { top: "3.5–4.5", bobbin: "Loose (2.5–3)"  };
  // High jump ratio → slightly loosen bobbin to reduce puckering
  if (jumpRatio > 0.25 && !isSatin) tension = { ...tension, bobbin: "Loose (2–2.5)" };

  // ── Speed ────────────────────────────────────────────────────────────────
  let baseSpm = isSatin ? 650 : isRunning ? 950 : 800;
  if (isLarge)                                           baseSpm = Math.max(baseSpm - 100, 400);
  if (isSmall)                                           baseSpm = Math.max(baseSpm - 50,  400);
  if (fabric === "fleece" || fabric === "hat")           baseSpm = Math.min(baseSpm, 700);
  if (isDense)                                           baseSpm = Math.max(baseSpm - 100, 400);
  if (isVeryDense)                                       baseSpm = Math.max(baseSpm - 100, 400);
  if (manyColors)                                        baseSpm = Math.max(baseSpm - 50,  400);
  const finalSpm  = Math.round(baseSpm / 50) * 50;
  const speedLabel = finalSpm <= 600 ? "Slow" : finalSpm <= 800 ? "Medium" : "Fast";

  return {
    stabilizer,
    needle,
    tension,
    speed: { label: speedLabel, spm: finalSpm },
    // Meta — shown as context beneath the grid when stitch data available
    meta: stitchData ? {
      stitchCount,
      colorChanges,
      designW,
      designH,
    } : null,
  };
}

const Label = ({ children }) => (
  <div style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", letterSpacing: "0.7px", textTransform: "uppercase", marginBottom: "6px" }}>
    {children}
  </div>
);

const Row = ({ label, value, tooltip }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #f3f4f6" }}>
    {tooltip
      ? <Tooltip title={tooltip}><span style={{ fontSize: "13px", color: "#374151", cursor: "help", borderBottom: "1px dotted #d1d5db" }}>{label}</span></Tooltip>
      : <span style={{ fontSize: "13px", color: "#374151" }}>{label}</span>}
    <span style={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>{value}</span>
  </div>
);


const formatElapsed = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const StitchExportButton = ({
  selectedFormat, setSelectedFormat,
  selectedFabric,
  stitchSettings,
  embroidery, fileName, layerSelected,
  isConverting, conversionResult,
  onConvert, onDownload, onCancelConvert,
  aiAnalysis,
  aiLoading,
  svgJoined,
  targetWidth,
  targetHeight,
  // Hoop / size controls — mirror StitchSidebar so the user can resize the
  // design directly from the export modal without needing the right-rail
  // panel open. All optional so the component stays backward-compatible.
  selectedHoop,
  setSelectedHoop,
  setTargetWidth,
  setTargetHeight,
  lockAspectRatio,
  setLockAspectRatio,
  originalDimensions,
  // Account-save indicator props (optional — older callers can omit). When
  // wired up by the parent we show a small "Saved to your account" pill below
  // the Download button after a successful upload to Storage.
  stitchFileSaveStatus = "idle",
  savedFileMeta = null,
  exportOpen,
  onExportOpenChange,
}) => {
  const { user, userIsPremium, userIsEmbroideryPremium, refreshEmbroideryPremiumStatus } = useAuth();
  // On EmbroideryTrace, only an EmbroideryTrace membership gates export — not SvgTrace Pro
  const effectivePremium = isEmbroideryTrace ? userIsEmbroideryPremium : userIsPremium;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = exportOpen !== undefined ? exportOpen : internalOpen;
  const setOpen = useCallback((value) => {
    if (onExportOpenChange) onExportOpenChange(value);
    else setInternalOpen(value);
  }, [onExportOpenChange]);
  const [showIOSPaywall, setShowIOSPaywall] = useState(false);
  const [showWebPaywall, setShowWebPaywall] = useState(false);
  const pendingActionRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [technicalView, setTechnicalView] = useState(false);
  const [technicalPoints, setTechnicalPoints] = useState(false);
  const [technicalJumps, setTechnicalJumps] = useState(true);
  const [technicalColor, setTechnicalColor] = useState('all');
  const [technicalProgress, setTechnicalProgress] = useState(null);

  // Pulses the download button for a few seconds when a result arrives
  const [downloadPulse, setDownloadPulse] = useState(false);

  // ── Wizard state ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("stitch");
  const artworkRef = useRef(null);
  const [isExportingArtwork, setIsExportingArtwork] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [sizeUnit, setSizeUnit] = useState("mm"); // mm | inch
  const [showAllDetails, setShowAllDetails] = useState(false);

  // Editable download filename. Strips any extension from the design's name so
  // the user edits just the base; the format extension is appended on download.
  const defaultFileName = (String(fileName || "").replace(/\.[^/.]+$/, "").trim()) || "embroidery";
  const [exportFileName, setExportFileName] = useState(defaultFileName);

  // Reset the wizard each time the modal opens. Returning users who already
  // generated a file jump straight to Preview & Export; everyone else starts
  // at step 1 (Size).
  useEffect(() => {
    if (!open) return;
    setActiveTab("stitch");
    setWizardStep(conversionResult ? 2 : 1);
    setIsFullscreen(false);
    setExportFileName(defaultFileName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pulse the download button every time a new conversion result arrives
  // while the modal is open — draws the eye to the green button.
  const prevResultRef = useRef(null);
  useEffect(() => {
    if (!conversionResult?.data || conversionResult === prevResultRef.current) return;
    if (!open) return;
    prevResultRef.current = conversionResult;
    setDownloadPulse(true);
    const t = setTimeout(() => setDownloadPulse(false), 4000);
    return () => clearTimeout(t);
  }, [conversionResult, open]);

  // Preview canvas size. Default (windowed) matches the legacy 632×300 layout.
  // On mobile (< 600px) shrink to 220px tall so the canvas fits the viewport.
  // Download/Regenerate are now in the sticky modal footer (always visible).
  // In fullscreen we derive the size deterministically from window dimensions
  // minus known fixed chrome heights — avoids ResizeObserver feedback loops.
  const isMobileExport = typeof window !== "undefined" && window.innerWidth < 600;
  const WINDOWED_PREVIEW = { w: 632, h: isMobileExport ? 220 : 300 };
  // Fixed chrome heights inside the fullscreen modal:
  // header ~56px + body padding 12*2 + toolbar ~45px + info row ~30px + gaps ~20px
  const FS_CHROME_HEIGHT = 175;
  const FS_CHROME_WIDTH = 32; // body horizontal padding 16*2
  const previewWrapperRef = useRef(null);

  const computeFullscreenSize = useCallback(() => ({
    w: Math.max(300, window.innerWidth - FS_CHROME_WIDTH),
    h: Math.max(200, window.innerHeight - FS_CHROME_HEIGHT),
  }), []);

  const [fullscreenSize, setFullscreenSize] = useState(computeFullscreenSize);
  const previewSize = isFullscreen ? { ...fullscreenSize, h: Math.max(200, fullscreenSize.h - (technicalView ? (isMobileExport ? 150 : 90) : 0)) } : WINDOWED_PREVIEW;

  // Centering math for the design (200×218 design space) within the preview
  // viewport. A plain function (not hook) since it's called imperatively.
  const centerPan = (z, size) => ({
    x: (size.w - 200 * z) / 2,
    y: (size.h - 218 * z) / 2,
  });

  const requirePremium = useCallback((action) => {
    if (effectivePremium) {
      action?.();
      return;
    }
    pendingActionRef.current = action;
    if (isNativeiOS()) {
      setShowIOSPaywall(true);
    } else {
      setShowWebPaywall(true);
    }
  }, [effectivePremium]);

  const openExportModal = useCallback(() => {
    if (aiLoading) return;
    requirePremium(() => setOpen(true));
  }, [aiLoading, requirePremium, setOpen]);

  const [previewZoom, setPreviewZoom] = useState(2);
  const previewPanRef = useRef(centerPan(2, WINDOWED_PREVIEW));
  const [previewPan, setPreviewPan] = useState(previewPanRef.current);
  const [realStitchData, setRealStitchData] = useState(null);
  useEffect(() => { setTechnicalProgress(null); setTechnicalColor('all'); }, [realStitchData]);
  const [conversionElapsedSeconds, setConversionElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isConverting) {
      setConversionElapsedSeconds(0);
      return undefined;
    }
    setConversionElapsedSeconds(0);
    const timer = setInterval(() => {
      setConversionElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isConverting]);

  // Parse stitch data whenever a conversion result arrives
  useEffect(() => {
    if (!conversionResult?.data) {
      setRealStitchData(null);
      return;
    }
    try {
      const parsed = parseEmbroideryFile(conversionResult.data, selectedFormat);
      if (parsed) setRealStitchData(parsed);
    } catch (e) {
      console.warn("[StitchExportButton] stitch parse failed:", e);
    }
  }, [conversionResult, selectedFormat]);

  // Recompute fullscreen dimensions on window resize — NOT on element resize,
  // so there's no feedback loop between the canvas output size and the
  // measurement input.
  useEffect(() => {
    if (!isFullscreen) return;
    const onResize = () => {
      const next = computeFullscreenSize();
      setFullscreenSize((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isFullscreen, computeFullscreenSize]);

  // Recenter the design when toggling fullscreen or when the viewport
  // dimensions change (window resize). We key on the primitive numbers so
  // user-driven pan/zoom isn't clobbered during normal interaction.
  const prevSizeRef = useRef(previewSize);
  useEffect(() => {
    if (prevSizeRef.current.w === previewSize.w && prevSizeRef.current.h === previewSize.h) return;
    prevSizeRef.current = previewSize;
    const p = centerPan(previewZoom, previewSize);
    previewPanRef.current = p;
    setPreviewPan(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSize]);

  // Esc exits fullscreen — mirrors the rest of the editor.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isFullscreen]);

  // Extract the actual SVG layer fill colors so the preview uses the design's
  // real colors instead of whatever default thread colors the stitch file encodes.
  const svgLayerColors = useMemo(() => {
    if (!svgJoined?.children) return null;
    const fills = svgJoined.children
      .map((c) => c?.attributes?.fill || c?.attributes?.style?.match(/fill:\s*([^;]+)/)?.[1])
      .filter(Boolean);
    if (!fills.length) return null;
    // DST/PES only emit a color-change (new color block) when the thread color
    // differs from the previous object, so Ink/Stitch coalesces consecutive
    // same-color layers into ONE block. If we don't mirror that here, the
    // block→layer index mapping drifts: e.g. two consecutive black layers push
    // the next block's index by one, so a yellow body block lands on a black
    // entry and renders black. Collapse consecutive duplicate fills to realign.
    const norm = (c) => {
      let v = String(c).trim().toLowerCase();
      if (/^#[0-9a-f]{3}$/.test(v)) {
        v = "#" + v.slice(1).split("").map((d) => d + d).join("");
      }
      return v;
    };
    const collapsed = fills.filter((c, i) => i === 0 || norm(c) !== norm(fills[i - 1]));
    return collapsed.length ? collapsed : null;
  }, [svgJoined]);

  const realStitchDataWithColors = useMemo(() => {
    if (!realStitchData) return null;
    if (!svgLayerColors?.length) return realStitchData;
    return { ...realStitchData, colors: svgLayerColors };
  }, [realStitchData, svgLayerColors]);

  const s = stitchSettings;
  const stitchType = s.stitch_type || s.fill_method || "auto_fill";
  const hasResult = !!conversionResult;

  // Relative fabric compensation summary for the note shown in step 3.
  const fabricAdjustment = describeFabricCompensation(selectedFabric);
  const selectedFabricLabel = (FABRIC_PRESETS.find((f) => f.value === selectedFabric)?.label || "").replace(/^\S+\s/, "");

  const buildRec = useCallback(() => computeMachineRec({
    fabric: selectedFabric,
    stitchType,
    targetW: targetWidth,
    targetH: targetHeight,
    stitchData: realStitchData,
  }), [selectedFabric, stitchType, targetWidth, targetHeight, realStitchData]);

  const [machineRec, setMachineRec] = useState(() => buildRec());
  const [recUpdated, setRecUpdated] = useState(false);

  // Recompute whenever settings change (pre-generation) or stitch data arrives (post-generation)
  useEffect(() => {
    setMachineRec(buildRec());
    if (realStitchData) {
      setRecUpdated(true);
      const t = setTimeout(() => setRecUpdated(false), 2000);
      return () => clearTimeout(t);
    }
  }, [buildRec, realStitchData]);

  const machineItems = [
    { label: "Stabilizer", value: `${machineRec.stabilizer.weight} ${machineRec.stabilizer.type}` },
    { label: "Needle",     value: `${machineRec.needle.type} ${machineRec.needle.size}` },
    { label: "Tension",    value: `Top ${machineRec.tension.top} · Bobbin ${machineRec.tension.bobbin}` },
    { label: "Speed",      value: machineRec.speed.label, subValue: `${machineRec.speed.spm} SPM` },
  ];

  // Design Details — measured directly from the generated stitch file. DST/PES
  // native units are 0.1 mm. Counts/lengths are derived from the decoded stitch
  // list so they reflect the actual machine output, not the pre-gen estimate.
  const designDetails = useMemo(() => {
    const d = realStitchData;
    if (!d?.stitches?.length) return null;
    const UNIT_MM = 0.1;
    const all = d.stitches;
    const needles = all.filter((st) => !st.jump);
    const uniqueColors = new Set(all.map((st) => st.colorIndex)).size;
    const colorChanges = Math.max(0, uniqueColors - 1);

    // The parser exposes jump runs, not confirmed trim commands.
    // Include the initial positioning move, and label this count accordingly.
    let travelMoves = 0;
    for (let i = 0; i < all.length; i++) {
      if (all[i].jump && (i === 0 || !all[i - 1].jump)) travelMoves++;
    }

    // Thread used = summed length of stitched (non-jump) segments.
    let lenUnits = 0;
    for (let i = 1; i < all.length; i++) {
      if (all[i].jump) continue;
      lenUnits += Math.hypot(all[i].x - all[i - 1].x, all[i].y - all[i - 1].y);
    }
    const topThreadM = (lenUnits * UNIT_MM) / 1000;

    const widthMm = Math.round((d.maxX - d.minX) * UNIT_MM);
    const heightMm = Math.round((d.maxY - d.minY) * UNIT_MM);
    const toIn = (mm) => (mm / 25.4).toFixed(2);
    const spm = machineRec?.speed?.spm || 800;

    return {
      format: (d.format || selectedFormat || "").toUpperCase(),
      widthMm, heightMm, widthIn: toIn(widthMm), heightIn: toIn(heightMm),
      stitchCount: needles.length,
      colorChanges,
      stops: colorChanges,
      runMin: Math.max(1, Math.round(all.length / spm)),
      travelMoves,
      topThreadM,
      bobbinM: topThreadM * 0.37, // bobbin ≈ ⅓–⅖ of top thread (rule of thumb)
    };
  }, [realStitchData, machineRec, selectedFormat]);

  // Hoop / size handlers — same logic as StitchSidebar so the two stay in
  // perfect sync (we deliberately don't share via props because the export
  // modal must work even when the sidebar is closed on mobile).
  const sizeControlsAvailable =
    !!setSelectedHoop && !!setTargetWidth && !!setTargetHeight;

  const handleHoopChange = (idx) => {
    setSelectedHoop?.(idx);
    const h = HOOP_SIZES[idx];
    if (h?.width && h?.height) {
      const ar = originalDimensions?.aspectRatio || 1;
      // Fit into the hoop's *usable* sewing field, not its nominal edge. Home
      // machines refuse to display a design that reaches the hoop limit, and a
      // PES at exactly 100mm tips over once pull-comp/expand/satin underlay are
      // added — so a 4×4 design vanishes on the machine. Leaving a few mm per
      // side keeps the generated file under the limit and visible.
      const usableW = safeHoopDim(h.width);
      const usableH = safeHoopDim(h.height);
      const hoopAspect = usableW / usableH;
      if (ar > hoopAspect) {
        setTargetWidth?.(usableW);
        setTargetHeight?.(Math.round(usableW / ar));
      } else {
        setTargetHeight?.(usableH);
        setTargetWidth?.(Math.round(usableH * ar));
      }
    }
  };

  const handleWidthChange = (w) => {
    setTargetWidth?.(w);
    if (lockAspectRatio && w && originalDimensions) {
      setTargetHeight?.(Math.round(w / originalDimensions.aspectRatio));
    }
  };

  const handleHeightChange = (h) => {
    setTargetHeight?.(h);
    if (lockAspectRatio && h && originalDimensions) {
      setTargetWidth?.(Math.round(h * originalDimensions.aspectRatio));
    }
  };

  // mm ↔ inch display helpers for the size step.
  const mmToInch = (mm) => (mm / 25.4).toFixed(2);
  const inchToMm = (inch) => Math.round(inch * 25.4);
  const sizeFieldValue = (mm) => (sizeUnit === "mm" ? mm : parseFloat(mmToInch(mm)));
  const sizeFieldToMm = (val) => (sizeUnit === "mm" ? val : inchToMm(val));

  // A size field has to be allowed to sit empty while it's being retyped, and
  // the editor can't hold that state: it floors a missing size at 100mm, which
  // refills the box under the caret the moment it's cleared. So the field shows
  // what was typed while it has focus, hands the editor a size only once the
  // typing amounts to a usable one, and goes back to the editor's value on blur.
  const [sizeDraft, setSizeDraft] = useState({});
  const sizeLimits = sizeUnit === "mm" ? { min: 10, max: 500 } : { min: 0.4, max: 20 };

  useEffect(() => { setSizeDraft({}); }, [sizeUnit]);

  const sizeField = (name, current, apply) => ({
    value: sizeDraft[name] !== undefined ? sizeDraft[name] : sizeFieldValue(current),
    onChange: (typed) => {
      setSizeDraft((draft) => ({ ...draft, [name]: typed }));
      if (typeof typed === "number" && typed >= sizeLimits.min && typed <= sizeLimits.max) {
        apply(sizeFieldToMm(typed));
      }
    },
    onBlur: () => {
      const typed = sizeDraft[name];
      if (typeof typed === "number") {
        apply(sizeFieldToMm(Math.min(Math.max(typed, sizeLimits.min), sizeLimits.max)));
      }
      setSizeDraft((draft) => ({ ...draft, [name]: undefined }));
    },
    min: sizeLimits.min,
    max: sizeLimits.max,
    step: sizeUnit === "mm" ? 5 : 0.5,
  });

  const closeModal = () => { setIsFullscreen(false); setOpen(false); };

  // ── Shared button styles ───────────────────────────────────────────────────
  const primaryBtn = {
    flex: 1, padding: "12px 16px",
    background: "linear-gradient(135deg,#F453C4 0%,#c7429f 100%)",
    border: "none", borderRadius: "10px", color: "#fff",
    fontWeight: 700, fontSize: "14px", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
    fontFamily: "Clash Grotesk",
    boxShadow: "0 3px 12px rgba(244,83,196,0.35)",
  };
  const ghostBtn = {
    padding: "12px 16px",
    background: "#f3f4f6", border: "1px solid #e5e7eb",
    borderRadius: "10px", color: "#374151",
    fontWeight: 600, fontSize: "13px", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
    fontFamily: "Clash Grotesk",
    flex: "0 0 auto",
  };

  // ── Format tabs ────────────────────────────────────────────────────────────
  // Same underlined-tab treatment as the SVG editor's export modal.
  const formatTabs = (
    <>
      <div style={{
        display: "flex", gap: "4px",
        padding: isMobileExport ? "10px 14px 0" : "12px 24px 0",
        overflowX: "auto", WebkitOverflowScrolling: "touch",
        msOverflowStyle: "none", scrollbarWidth: "none", flexShrink: 0,
      }}>
        {FORMAT_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: "0 0 auto",
                display: "flex", alignItems: "center", gap: "5px",
                padding: "8px 14px",
                border: "none", borderRadius: "8px 8px 0 0",
                background: isActive ? "#f8f0ff" : "transparent",
                color: isActive ? "#c7429f" : "#777",
                fontWeight: isActive ? 600 : 500,
                fontSize: "13px", fontFamily: "DM Sans, sans-serif",
                cursor: "pointer", transition: "all 0.15s ease",
                borderBottom: isActive ? "2px solid #F453C4" : "2px solid transparent",
                whiteSpace: "nowrap",
              }}
            >
              <IonIcon icon={tab.icon} style={{ fontSize: "15px" }} />
              {tab.label}
            </button>
          );
        })}
      </div>
      <div style={{ height: "1px", background: "#f0f0f0", margin: isMobileExport ? "0 14px" : "0 24px", flexShrink: 0 }} />
    </>
  );

  // ── Step indicator ─────────────────────────────────────────────────────────
  const stepIndicator = (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: isMobileExport ? "10px 12px 4px" : "14px 24px 6px",
    }}>
      {STEPS.map((step, idx) => {
        const reachable = step.num <= wizardStep || hasResult;
        const done = step.num < wizardStep;
        const active = step.num === wizardStep;
        return (
          <div key={step.num} style={{ display: "flex", alignItems: "center" }}>
            <div
              onClick={() => { if (reachable) setWizardStep(step.num); }}
              style={{
                display: "flex", alignItems: "center", gap: "6px",
                cursor: reachable ? "pointer" : "default",
                opacity: reachable ? 1 : 0.4,
                transition: "all 0.2s ease",
              }}
            >
              <div style={{
                width: "24px", height: "24px", borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: 700,
                background: done ? "#16a34a" : active ? "#F453C4" : "#e5e7eb",
                color: done || active ? "#fff" : "#9ca3af",
                transition: "all 0.2s ease",
              }}>
                {done ? <CheckOutlined style={{ fontSize: "11px" }} /> : step.num}
              </div>
              {!isMobileExport && (
                <span style={{
                  fontSize: "12px",
                  fontWeight: active ? 700 : 500,
                  color: active ? "#111" : done ? "#16a34a" : "#9ca3af",
                  fontFamily: "Clash Grotesk, sans-serif",
                }}>
                  {step.label}
                </span>
              )}
            </div>
            {idx < STEPS.length - 1 && (
              <div style={{
                width: isMobileExport ? "24px" : "32px", height: "2px",
                background: done ? "#16a34a" : "#e5e7eb",
                margin: "0 8px", borderRadius: "1px",
                transition: "background 0.2s ease",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Step 1: Fabric ──────────────────────────────────────────────────────────
  // ── Step 1: Size ─────────────────────────────────────────────────────────────
  const step2 = (
    <>
      <div style={{ textAlign: "center", marginBottom: "4px" }}>
        <h3 style={{ margin: "0 0 2px", fontSize: "16px", fontWeight: 700, fontFamily: "Clash Grotesk, sans-serif", color: "#111" }}>
          Set your format &amp; size
        </h3>
        <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af" }}>
          Pick your machine's file type and hoop, then generate. The design keeps its proportions.
        </p>
      </div>

      {/* Machine format — chosen up front so the generated file is always the
          right type. Changing it after a generation clears the old file (the
          parent resets the result) so the download can never be a mismatched
          format. */}
      <div data-tutorial="export-format" style={{ padding: "14px", background: "#f9fafb", borderRadius: "10px", border: "1px solid #eef0f2" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#374151", fontFamily: "Clash Grotesk" }}>Machine format</div>
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px" }}>Choose your machine's file type</div>
          </div>
          <Select
            value={selectedFormat}
            onChange={setSelectedFormat}
            style={{ minWidth: 210, flex: "0 1 260px" }}
            options={MACHINE_FORMATS.map((f) => ({ value: f.value, label: f.label }))}
            aria-label="Machine format"
          />
        </div>
      </div>

      <div style={{ padding: "14px", background: "#f9fafb", borderRadius: "10px", border: "1px solid #eef0f2" }}>
        {/* Unit toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151", fontFamily: "Clash Grotesk" }}>Units</span>
          <Segmented
            size="small"
            value={sizeUnit}
            onChange={setSizeUnit}
            options={[{ value: "mm", label: "mm" }, { value: "inch", label: "inch" }]}
          />
        </div>

        {/* Hoop preset */}
        {sizeControlsAvailable && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "6px" }}>Hoop size preset</div>
            <Select
              value={selectedHoop}
              onChange={handleHoopChange}
              style={{ width: "100%" }}
              options={HOOP_SIZES.map((h, i) => ({ value: i, label: h.label }))}
              aria-label="Hoop size"
            />
          </div>
        )}

        {/* Width / lock / height */}
        {sizeControlsAvailable && (
          <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
            <div data-tutorial="export-width" style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>
                Width {sizeUnit === "inch" ? "(inch)" : "(mm)"}
              </div>
              <InputNumber
                {...sizeField("width", targetWidth, handleWidthChange)}
                style={{ width: "100%" }}
                addonAfter={sizeUnit}
              />
            </div>

            <Tooltip title={lockAspectRatio ? "Aspect ratio locked" : "Aspect ratio unlocked"}>
              <button
                type="button"
                onClick={() => setLockAspectRatio?.(!lockAspectRatio)}
                style={{
                  border: "1px solid #e5e7eb",
                  background: lockAspectRatio ? "rgba(244,83,196,0.08)" : "#fff",
                  cursor: "pointer", padding: "0 8px", height: "32px",
                  borderRadius: "6px",
                  color: lockAspectRatio ? "#F453C4" : "#9ca3af",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: "1px",
                }}
              >
                {lockAspectRatio ? <LockOutlined /> : <UnlockOutlined />}
              </button>
            </Tooltip>

            <div data-tutorial="export-height" style={{ flex: 1 }}>
              <div style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "4px" }}>
                Height {sizeUnit === "inch" ? "(inch)" : "(mm)"}
              </div>
              <InputNumber
                {...sizeField("height", targetHeight, handleHeightChange)}
                style={{ width: "100%" }}
                addonAfter={sizeUnit}
              />
            </div>
          </div>
        )}

        {/* Output summary */}
        <div style={{
          marginTop: "12px", padding: "8px 12px", background: "#fff",
          borderRadius: "6px", fontSize: "12px", color: "#6b7280",
          display: "flex", justifyContent: "space-between",
        }}>
          <span>Output: <strong style={{ color: "#111" }}>{targetWidth}×{targetHeight} mm</strong></span>
          <span>({mmToInch(targetWidth)}×{mmToInch(targetHeight)} inch)</span>
        </div>
      </div>
    </>
  );

  const technicalEnd = realStitchData?.stitches[(technicalProgress ?? realStitchData?.stitches.length ?? 0) - 1];
  const technicalStart = realStitchData?.stitches[(technicalProgress ?? realStitchData?.stitches.length ?? 0) - 2];
  const technicalLength = technicalEnd && technicalStart && (technicalEnd.jump || technicalEnd.colorIndex === technicalStart.colorIndex)
    ? (Math.hypot(technicalEnd.x - technicalStart.x, technicalEnd.y - technicalStart.y) / 10).toFixed(2) : null;

  // ── Preview block (used by step 3 and fullscreen) ───────────────────────────
  const previewBlock = (
    <div style={isFullscreen
      ? { display: "flex", flexDirection: "column", gap: "0px", flex: 1, minHeight: 0, borderRadius: "12px", overflow: "hidden", border: "1px solid #e5e7eb" }
      : { display: "flex", flexDirection: "column", gap: "0px", borderRadius: "12px", overflow: "hidden", border: "1px solid #e5e7eb" }}>

      {/* Toolbar: format + zoom + fullscreen */}
      <div style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px",
        padding: "10px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: "1 1 auto", minWidth: 0 }}>
          <span style={{ fontSize: "11px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
            Format
          </span>
          {/* Read-only here — the format is chosen on the Size step so the
              generated bytes always match the selected type. To switch, go
              back to Size (which clears this result and requires a regenerate). */}
          <Tooltip title="Change the format on the Size step, then regenerate">
            <span style={{
              fontSize: "12px", fontWeight: 700, color: "#374151",
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: "6px",
              padding: "3px 10px", whiteSpace: "nowrap", cursor: "default",
            }}>
              {selectedFormat.toUpperCase()}
            </span>
          </Tooltip>
        </div>

        <button disabled={!realStitchData} aria-pressed={technicalView}
          onClick={() => setTechnicalView(v => !v)}
          style={{ padding: '4px 10px', border: '1px solid #cbd5e1', borderRadius: 5, background: technicalView ? '#dbeafe' : '#fff', cursor: 'pointer' }}>
          Technical view
        </button>
        {/* Zoom controls */}
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <button
            onClick={() => {
              setPreviewZoom((z) => {
                const newZ = Math.max(0.5, z / 1.5);
                const cx = previewSize.w / 2, cy = previewSize.h / 2;
                const sc = newZ / z;
                const p = { x: cx - sc * (cx - previewPanRef.current.x), y: cy - sc * (cy - previewPanRef.current.y) };
                previewPanRef.current = p; setPreviewPan(p); return newZ;
              });
            }}
            style={{ fontSize: "12px", width: "26px", height: "26px", border: "1px solid #e5e7eb", borderRadius: "5px", background: "#fff", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}
          >−</button>
          <button
            onClick={() => { setPreviewZoom(2); const p = centerPan(2, previewSize); previewPanRef.current = p; setPreviewPan(p); }}
            style={{ fontSize: "10px", padding: "0 8px", height: "26px", border: "1px solid #e5e7eb", borderRadius: "5px", background: "#fff", cursor: "pointer", color: "#6b7280" }}
          >Reset</button>
          <button
            onClick={() => {
              setPreviewZoom((z) => {
                const newZ = Math.min(10, z * 1.5);
                const cx = previewSize.w / 2, cy = previewSize.h / 2;
                const sc = newZ / z;
                const p = { x: cx - sc * (cx - previewPanRef.current.x), y: cy - sc * (cy - previewPanRef.current.y) };
                previewPanRef.current = p; setPreviewPan(p); return newZ;
              });
            }}
            style={{ fontSize: "12px", width: "26px", height: "26px", border: "1px solid #e5e7eb", borderRadius: "5px", background: "#fff", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", justifyContent: "center" }}
          >+</button>
          <Tooltip title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen preview"}>
            <button
              onClick={() => setIsFullscreen((v) => !v)}
              style={{ fontSize: "12px", width: "26px", height: "26px", border: "1px solid #e5e7eb", borderRadius: "5px", background: isFullscreen ? "#fdf0fa" : "#fff", cursor: "pointer", color: isFullscreen ? "#F453C4" : "#6b7280", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: "4px" }}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </button>
          </Tooltip>
        </div>
      </div>

      {technicalView && realStitchData && <div style={{ padding: '8px 12px', background: '#fff', fontSize: 12, color: '#334155', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <label><input type="checkbox" checked={technicalPoints} onChange={e => setTechnicalPoints(e.target.checked)} /> Needle points</label>
        <label><input type="checkbox" checked={technicalJumps} onChange={e => setTechnicalJumps(e.target.checked)} /> Jumps (red dashed)</label>
        <label>Color block <select style={{ background: "#fff", color: "#172554", border: "1px solid #94a3b8", borderRadius: 4 }} aria-label="Color block" value={technicalColor} onChange={e => setTechnicalColor(e.target.value)}>
          <option value="all">All</option>
          {realStitchData.colors.map((_, i) => <option key={i} value={i}>Block {i + 1}</option>)}
        </select></label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Sequence
          <input aria-label="Stitch sequence" type="range" min="0" max={realStitchData.stitches.length} value={technicalProgress ?? realStitchData.stitches.length} onChange={e => setTechnicalProgress(Number(e.target.value))} />
          <input aria-label="Sequence position" type="number" min="0" max={realStitchData.stitches.length} style={{ width: 75, background: "#fff", color: "#172554", border: "1px solid #94a3b8", borderRadius: 4 }} value={technicalProgress ?? realStitchData.stitches.length} onChange={e => setTechnicalProgress(Math.max(0, Math.min(realStitchData.stitches.length, Number(e.target.value))))} />
          / {realStitchData.stitches.length}
        </label>
        {technicalLength !== null && <span>Last move: {technicalLength} mm {technicalEnd.jump ? 'jump' : 'sewn'} · X {(technicalEnd.x / 10).toFixed(1)}, Y {(technicalEnd.y / 10).toFixed(1)} mm</span>}
        <span style={{ flexBasis: '100%', color: '#64748b' }}>Actual file paths · 1 mm grid · contrasting block colors · no thread shading. Sequence includes jumps. Underlay, travel and tie stitches are not separately identified in this file view.</span>
      </div>}
      {/* Preview wrapper */}
      <div
        ref={previewWrapperRef}
        style={isFullscreen
          ? { position: "relative", width: "100%", flex: 1, minHeight: 0 }
          : { position: "relative", width: "100%" }}
      >
        <StitchPreviewCanvas
          svgJoined={svgJoined}
          stitchSettings={stitchSettings}
          targetWidth={targetWidth ?? 100}
          targetHeight={targetHeight ?? 100}
          realStitchData={realStitchDataWithColors}
          technical={technicalView ? { points: technicalPoints, jumps: technicalJumps, color: technicalColor, progress: technicalProgress ?? realStitchData?.stitches.length ?? 0 } : null}
          zoom={previewZoom}
          setZoom={setPreviewZoom}
          pan={previewPan}
          setPan={setPreviewPan}
          panRef={previewPanRef}
          viewportWidth={previewSize.w}
          viewportHeight={previewSize.h}
        />

        {/* Centre overlay: Generate button (no result) or Converting spinner */}
        {!hasResult && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: isConverting ? "rgba(245,239,229,1)" : "rgba(245,239,229,0.35)",
            pointerEvents: isConverting ? "none" : "auto",
          }}>
            {isConverting ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", maxWidth: "320px", padding: "0 18px", textAlign: "center" }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: "10px",
                  background: "rgba(255,255,255,0.85)", borderRadius: "40px",
                  padding: "12px 22px", boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                }}>
                  <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#F453C4", display: "inline-block", animation: "pulse 1s infinite" }} />
                  <span style={{ fontSize: "14px", fontWeight: 600, color: "#F453C4", fontFamily: "Clash Grotesk" }}>Converting…</span>
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#6b7280", fontFamily: "Clash Grotesk", fontVariantNumeric: "tabular-nums" }}>
                    {formatElapsed(conversionElapsedSeconds)}
                  </span>
                </div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: "#555", lineHeight: 1.35, fontFamily: "Clash Grotesk" }}>
                  {isEmbroideryTrace
                    ? "Generating stitches. Complex designs may take several minutes."
                    : "Generating stitches. Complex designs may take several minutes."}
                </div>
                {onCancelConvert && (
                  <button onClick={onCancelConvert} style={{ background: "none", border: "1px solid rgba(0,0,0,0.2)", borderRadius: "40px", padding: "5px 16px", fontSize: "12px", color: "#666", cursor: "pointer", fontFamily: "Clash Grotesk", fontWeight: 500 }}>
                    Cancel
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", textAlign: "center", padding: "0 18px" }}>
                <button
                  data-tutorial="generate-stitch-file"
                  onClick={() => requirePremium(onConvert)}
                  style={{ padding: "14px 32px", background: "linear-gradient(135deg,#F453C4 0%,#c7429f 100%)", border: "none", borderRadius: "40px", color: "#fff", fontWeight: 700, fontSize: "15px", letterSpacing: "0.3px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 6px 24px rgba(244,83,196,0.5)", transition: "transform 0.15s, box-shadow 0.15s", fontFamily: "Clash Grotesk" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(244,83,196,0.6)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 6px 24px rgba(244,83,196,0.5)"; }}
                >
                  <ThunderboltOutlined style={{ fontSize: "15px" }} />
                  Generate Stitch File
                </button>
                <span style={{ fontSize: "11px", color: "#8a7f72", fontFamily: "Clash Grotesk" }}>
                  Creates the real machine stitches for your {selectedFormat.toUpperCase()} file
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {(realStitchDataWithColors || hasResult) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "11px", color: "#6b7280", padding: "8px" }}>
          {realStitchDataWithColors && <span>{realStitchDataWithColors.stitches?.length?.toLocaleString()} stitches · scroll to zoom · drag to pan</span>}
          {hasResult && stitchFileSaveStatus === "saving" && <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}><CloudUploadOutlined style={{ fontSize: "11px", color: "#F453C4" }} />Saving to your account…</span>}
          {hasResult && stitchFileSaveStatus === "saved" && (
            <Tooltip title={savedFileMeta ? `Saved ${new Date(savedFileMeta.generatedAt).toLocaleString()} · ${(savedFileMeta.sizeBytes / 1024).toFixed(1)} KB` : "Saved to your account"}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", color: "#16a34a", cursor: "default" }}>
                <CheckCircleFilled style={{ fontSize: "11px" }} />Saved to your account
              </span>
            </Tooltip>
          )}
          {hasResult && stitchFileSaveStatus === "error" && <span style={{ color: "#b91c1c" }}>Couldn't save to your account — re-download to keep a copy.</span>}
        </div>
      )}
    </div>
  );

  // Hoop-fit warning from the converter — a design larger than the selected hoop
  // is silently hidden by most machines, so flag it loudly before download.
  const hoopFit = conversionResult?.metadata?.hoop_fit;
  const hoopOverflow = hoopFit && hoopFit.fits === false ? hoopFit : null;

  // ── Step 2: Preview & Export ────────────────────────────────────────────────
  const step3 = (
    <>
      {hoopOverflow && !isFullscreen && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: "8px",
          fontSize: "12px", color: "#9a3412", lineHeight: 1.4,
          background: "#fff7ed", border: "1px solid #fed7aa",
          borderRadius: "10px", padding: "10px 12px",
        }}>
          <span style={{ fontSize: "14px", lineHeight: 1, flexShrink: 0 }} aria-hidden>⚠️</span>
          <span>
            <strong>Design won't fit your hoop.</strong>{" "}
            It's {hoopOverflow.designMm?.width}×{hoopOverflow.designMm?.height}mm but the selected hoop is {hoopOverflow.hoopMm?.width}×{hoopOverflow.hoopMm?.height}mm. Most machines hide a design that doesn't fit — go back to <strong>Size</strong> and shrink it (or pick a larger hoop) before stitching.
          </span>
        </div>
      )}

      {/* Preview label so the canvas is clearly the focus of this step */}
      {!isFullscreen && (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Label>Preview</Label>
          {recUpdated && (
            <span style={{
              fontSize: "10px", fontWeight: 700, color: "#F453C4",
              background: "rgba(244,83,196,0.08)", borderRadius: "6px",
              padding: "1px 7px", letterSpacing: "0.3px",
              animation: "fadeInOut 2s ease forwards",
            }}>
              ✦ Updated
            </span>
          )}
        </div>
      )}

      {previewBlock}

      {/* At-a-glance: run time hero + the few metrics worth scanning */}
      {!isFullscreen && designDetails && (
        <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
          <div style={{
            flex: "0 0 auto", minWidth: "118px",
            padding: "10px 14px", borderRadius: "10px",
            background: "linear-gradient(135deg,rgba(244,83,196,0.09),rgba(124,58,237,0.09))",
            border: "1px solid rgba(124,58,237,0.15)",
            display: "flex", flexDirection: "column", justifyContent: "center",
          }}>
            <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>Run time</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#111", fontFamily: "Clash Grotesk", lineHeight: 1.15 }}>
              ~{designDetails.runMin} min
            </div>
            <div style={{ fontSize: "10px", color: "#9ca3af" }}>at {machineRec.speed.spm} SPM</div>
          </div>
          <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 14px", alignContent: "center" }}>
            {[
              { label: "Stitches", value: designDetails.stitchCount.toLocaleString() },
              { label: "Colors", value: designDetails.colorChanges + 1 },
              { label: "Size", value: `${designDetails.widthMm}×${designDetails.heightMm} mm` },
              { label: "Thread", value: `~${designDetails.topThreadM.toFixed(0)} m` },
            ].map((c, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "6px", borderBottom: "1px solid #f3f4f6", padding: "3px 0" }}>
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>{c.label}</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>{c.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fabric compensation note — fabric tweaked density/pull-comp on top of your settings */}
      {!isFullscreen && designDetails && fabricAdjustment && (
        <Tooltip title="Applied on top of your per-layer settings to compensate for how this fabric behaves — your settings are never overwritten. Change fabric in step 1 and regenerate to update.">
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            fontSize: "11px", color: "#7C3AED",
            background: "rgba(124,58,237,0.05)", border: "1px solid rgba(124,58,237,0.12)",
            borderRadius: "8px", padding: "6px 10px", cursor: "default",
          }}>
            <SettingOutlined style={{ fontSize: "12px", flexShrink: 0 }} />
            <span><strong>{selectedFabricLabel}</strong> fabric adjustment: {fabricAdjustment}</span>
          </div>
        </Tooltip>
      )}

      {/* Everything else (machine setup + full breakdown) collapsed by default */}
      {!isFullscreen && designDetails && (
        <div>
          <div
            onClick={() => setShowAllDetails((v) => !v)}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "12px", color: showAllDetails ? "#F453C4" : "#9ca3af", cursor: "pointer", padding: "2px 0" }}
          >
            <SettingOutlined />
            {showAllDetails ? "Hide machine setup & details" : "Machine setup & full details"}
          </div>

          {showAllDetails && (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
              {/* Machine Setup */}
              <div>
                <Label>Machine Setup</Label>
                <div style={{
                  padding: "12px 14px", borderRadius: "10px",
                  background: "linear-gradient(135deg,rgba(244,83,196,0.04),rgba(124,58,237,0.04))",
                  border: "1px solid rgba(124,58,237,0.1)",
                  display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px",
                }}>
                  {machineItems.map((item, i) => (
                    <div key={i}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>{item.label}</div>
                      <div style={{ marginTop: "2px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>{item.value}</span>
                        {item.subValue && (
                          <span style={{ fontSize: "11px", color: "#9ca3af", fontWeight: 500, marginLeft: "5px" }}>{item.subValue}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Full design breakdown */}
              <div>
                <Label>Full Breakdown</Label>
                <div style={{
                  padding: "2px 14px", borderRadius: "10px",
                  border: "1px solid rgba(124,58,237,0.1)",
                  background: "linear-gradient(135deg,rgba(244,83,196,0.04),rgba(124,58,237,0.04))",
                }}>
                  <Row label="Format" value={designDetails.format} />
                  <Row label="Width" value={`${designDetails.widthIn}" (${designDetails.widthMm} mm)`} />
                  <Row label="Height" value={`${designDetails.heightIn}" (${designDetails.heightMm} mm)`} />
                  <Row label="Color Changes" value={designDetails.colorChanges} />
                  <Row label="Stops" value={designDetails.stops} />
                  <Row label="Travel moves" tooltip="Groups of jump movements, including initial positioning. These are not confirmed thread trims." value={designDetails.travelMoves} />
                  <Row label="Bobbin Thread" tooltip="Estimated at ~⅓ of top thread usage" value={`~${designDetails.bobbinM.toFixed(0)} m`} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  // ── Artwork tabs (SVG / PNG) ────────────────────────────────────────────────
  const handleArtworkDownload = async () => {
    setIsExportingArtwork(true);
    try {
      await artworkRef.current?.export();
    } finally {
      setIsExportingArtwork(false);
    }
  };

  const artworkFooter = (
    <div style={{ display: "flex", gap: "8px", padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 }}>
      <button
        onClick={handleArtworkDownload}
        disabled={isExportingArtwork || !svgJoined}
        style={{
          ...primaryBtn,
          cursor: isExportingArtwork ? "wait" : "pointer",
          opacity: isExportingArtwork ? 0.85 : 1,
        }}
        onMouseEnter={(e) => { if (!isExportingArtwork) e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
      >
        <DownloadOutlined style={{ fontSize: "14px" }} />
        {isExportingArtwork ? "Downloading…" : `Download ${activeTab.toUpperCase()}`}
      </button>
    </div>
  );

  // ── Footer (per step) ────────────────────────────────────────────────────────
  let footer = null;
  if (activeTab !== "stitch") {
    footer = artworkFooter;
  } else if (wizardStep === 1) {
    footer = (
      <div style={{ display: "flex", gap: "8px", padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 }}>
        <button data-tutorial="export-next-preview" style={primaryBtn} onClick={() => setWizardStep(2)}>
          Next — Preview &amp; export →
        </button>
      </div>
    );
  } else if (wizardStep === 2) {
    footer = (
      <div style={{ display: "flex", gap: "8px", padding: "12px 16px", borderTop: "1px solid #f0f0f0", background: "#fff", flexShrink: 0 }}>
        <button style={ghostBtn} onClick={() => setWizardStep(1)}>← Back</button>
        {hasResult && (
          <>
            <Input
              value={exportFileName}
              onChange={(e) => setExportFileName(e.target.value)}
              placeholder="embroidery"
              maxLength={80}
              addonAfter={`.${selectedFormat}`}
              aria-label="Export file name"
              style={{ flex: "1 1 140px", minWidth: 0 }}
            />
            <button
              data-tutorial="export-download"
              onClick={() => { setDownloadPulse(false); requirePremium(() => onDownload?.(exportFileName)); }}
              style={{
                flex: "0 0 auto", padding: "12px 16px",
                background: "linear-gradient(135deg,#22c55e 0%,#16a34a 100%)",
                border: "none", borderRadius: "10px", color: "#fff",
                fontWeight: 700, fontSize: "14px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "7px",
                boxShadow: downloadPulse
                  ? "0 0 0 4px rgba(34,197,94,0.35), 0 3px 12px rgba(34,197,94,0.45)"
                  : "0 3px 12px rgba(34,197,94,0.45)",
                transition: "transform 0.15s, box-shadow 0.15s",
                fontFamily: "Clash Grotesk",
                animation: downloadPulse ? "download-pulse 0.8s ease-in-out infinite alternate" : "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}
            >
              <DownloadOutlined style={{ fontSize: "14px" }} />
              Download
            </button>
            <button
              disabled={isConverting}
              onClick={() => requirePremium(onConvert)}
              style={{
                padding: "12px 16px",
                background: "#f3f4f6", border: "1px solid #e5e7eb",
                borderRadius: "10px", color: "#374151",
                fontWeight: 600, fontSize: "13px",
                cursor: isConverting ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "6px",
                transition: "background 0.15s", fontFamily: "Clash Grotesk",
                opacity: isConverting ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!isConverting) e.currentTarget.style.background = "#e5e7eb"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#f3f4f6"; }}
            >
              <ThunderboltOutlined style={{ fontSize: "13px" }} />
              {isConverting ? "Converting…" : "Regenerate"}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Tooltip
        title={aiLoading ? "Preparing your design…" : "Export a machine-ready stitch file (DST, PES, JEF…)"}
        placement="bottom"
        mouseEnterDelay={0.3}
      >
        <button
          onClick={openExportModal}
          onMouseEnter={() => !aiLoading && setIsHovered(true)}
          onMouseLeave={() => { setIsHovered(false); setIsPressed(false); }}
          onMouseDown={() => !aiLoading && setIsPressed(true)}
          onMouseUp={() => setIsPressed(false)}
          disabled={aiLoading}
          aria-label={isEmbroideryTrace ? "Export Stitch" : "Export"}
          aria-busy={aiLoading || undefined}
          style={{
            fontWeight: 700, fontFamily: "Clash Grotesk", letterSpacing: "0.01em",
            background: aiLoading
              ? "linear-gradient(135deg,#e8a8da 0%,#d68fc6 100%)"
              : "linear-gradient(135deg,#F453C4 0%,#c7429f 100%)",
            transition: "transform 0.12s ease, box-shadow 0.15s ease, filter 0.15s ease, opacity 0.15s ease",
            border: "none", color: "#fff",
            padding: "0 14px", borderRadius: "9px",
            cursor: aiLoading ? "wait" : "pointer",
            fontSize: "12px", whiteSpace: "nowrap",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
            height: "30px",
            filter: isHovered && !aiLoading ? "brightness(1.05)" : "none",
            transform: aiLoading ? "none" : isPressed ? "translateY(0) scale(0.97)" : isHovered ? "translateY(-1px)" : "none",
            boxShadow: isHovered && !aiLoading
              ? "0 5px 16px rgba(244,83,196,0.5)"
              : "0 3px 12px rgba(244,83,196,0.4)",
            opacity: aiLoading ? 0.9 : 1,
          }}
        >
          {aiLoading ? (
            <>
              <span style={{
                width: "12px", height: "12px", borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.45)", borderTopColor: "#fff",
                display: "inline-block", animation: "spin 0.7s linear infinite",
              }} />
              Preparing…
            </>
          ) : (
            isEmbroideryTrace ? "Export Stitch" : "Export"
          )}
        </button>
      </Tooltip>

      <Modal
        open={open}
        onCancel={closeModal}
        getContainer={() => document.body}
        width={isFullscreen ? "100vw" : 680}
        centered={!isFullscreen}
        closable={false}
        footer={null}
        // In fullscreen we anchor to the top-left and override the modal's
        // default max-width so the chrome can fill the viewport edge-to-edge.
        // Body becomes a flex column so the preview wrapper can flex-grow.
        style={isFullscreen ? { top: 0, paddingBottom: 0, maxWidth: "100vw" } : undefined}
        styles={{
          body: isFullscreen
            ? { padding: 0, height: "100dvh", display: "flex", flexDirection: "column" }
            : { padding: 0 },
          content: isFullscreen
            ? { borderRadius: 0, overflow: "hidden", padding: 0, height: "100dvh" }
            : { borderRadius: 16, overflow: "hidden", padding: 0 },
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: isMobileExport ? "14px 16px 10px 16px" : "20px 24px 14px 24px",
          borderBottom: isFullscreen ? "1px solid #f0f0f0" : "none",
        }}>
          <span style={{ fontSize: isMobileExport ? "17px" : "20px", fontWeight: 700, fontFamily: "Clash Grotesk", color: "#111" }}>
            Export Embroidery
          </span>
          <button onClick={closeModal} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", fontSize: "18px", padding: "4px",
            borderRadius: "6px", display: "flex", alignItems: "center",
            transition: "color 0.15s",
          }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#111"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#9ca3af"}
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Format tabs + wizard step indicator (both hidden in fullscreen) */}
        {!isFullscreen && formatTabs}
        {!isFullscreen && activeTab === "stitch" && stepIndicator}

        {/* Scrollable body. In fullscreen the body becomes a flex column with
            min-height: 0 so the preview wrapper can `flex: 1` and grow into
            the remaining vertical space without pushing other rows off-screen. */}
        <div style={isFullscreen ? {
          padding: "12px 16px",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        } : {
          padding: isMobileExport ? "12px 14px" : "16px 24px 20px",
          maxHeight: isMobileExport ? "70dvh" : "76vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          gap: isMobileExport ? "12px" : "16px",
        }}>
          {isFullscreen ? previewBlock : activeTab !== "stitch" ? (
            <StitchArtworkExport
              ref={artworkRef}
              svgJoined={svgJoined}
              format={activeTab}
              exportName={exportFileName}
              onExportNameChange={setExportFileName}
              isMobile={isMobileExport}
            />
          ) : (
            <>
              {wizardStep === 1 && step2}
              {wizardStep === 2 && step3}
            </>
          )}
        </div>

        {/* Footer (hidden in fullscreen) */}
        {!isFullscreen && footer}

      </Modal>
      <style>{`
        @keyframes fadeInOut {
          0%   { opacity: 0; transform: translateY(-2px); }
          15%  { opacity: 1; transform: translateY(0); }
          75%  { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes download-pulse {
          from { box-shadow: 0 0 0 2px rgba(34,197,94,0.25), 0 3px 12px rgba(34,197,94,0.45); transform: translateY(0); }
          to   { box-shadow: 0 0 0 7px rgba(34,197,94,0.12), 0 6px 20px rgba(34,197,94,0.55); transform: translateY(-2px); }
        }
      `}</style>
      {showIOSPaywall && (
        <IOSPaywall
          standalone
          action="export"
          onClose={() => setShowIOSPaywall(false)}
          onSuccess={() => setShowIOSPaywall(false)}
        />
      )}
      {showWebPaywall && (
        <PaywallModal
          isVisible={showWebPaywall}
          onClose={() => { setShowWebPaywall(false); pendingActionRef.current = null; }}
          onSuccess={() => {
            setShowWebPaywall(false);
            if (isEmbroideryTrace && refreshEmbroideryPremiumStatus) {
              refreshEmbroideryPremiumStatus();
            }
            pendingActionRef.current?.();
            pendingActionRef.current = null;
          }}
          action="export"
          isAuthenticated={!!user}
          isPremium={!!effectivePremium}
        />
      )}
    </>
  );
};

export default StitchExportButton;
